// Central data store for all page content in the application
// This provides a single source of truth for page content that can be imported
// and used across different components

// ===============================================================
// DISPLAY DATA - Used for UI Components
// ===============================================================

// Practice Areas Display Data - Used by PracticeAreasSection component
export const practiceAreasDisplay = {
  litigation: {
    title: "Litigation & Dispute Resolution",
    description: "We represent clients at all levels of the Indian judicial system including District Courts, High Courts, and specialized tribunals.",
    areas: [
      { name: "Civil Suits", path: "/civil-disputes" },
      { name: "Criminal Defense", path: "/criminal-case" },
      { name: "Constitutional Matters", path: "/constitutional-matters" },
      { name: "Arbitration & Mediation", path: "/arbitration" },
      { name: "Commercial Disputes", path: "/commercial-disputes" },
      { name: "Writ Petitions", path: "/high-court" }
    ]
  },
  corporate: {
    title: "Corporate & Commercial Law",
    description: "We provide comprehensive legal support to companies, startups, and investors navigating the complex business environment in India.",
    areas: [
      { name: "Company Formation", path: "/company-formation" },
      { name: "Shareholders' Agreements", path: "/shareholders-agreements" },
      { name: "Commercial Contracts", path: "/contract-management" },
      { name: "Corporate Governance", path: "/corporate-governance" },
      { name: "Regulatory Compliance", path: "/regulatory-compliance" },
      { name: "Due Diligence", path: "/due-diligence" }
    ]
  },
  property: {
    title: "Real Estate & Property Law",
    description: "We handle all aspects of property transactions, title disputes, and real estate regulatory compliance across India.",
    areas: [
      { name: "Title Due Diligence", path: "/title-due-diligence" },
      { name: "Sale & Lease Agreements", path: "/sale-lease-agreements" },
      { name: "Property Registration", path: "/property-registration" },
      { name: "RERA Compliance", path: "/rera-compliance" },
      { name: "Land Acquisition", path: "/land-acquisition" },
      { name: "Builder-Buyer Disputes", path: "/builder-buyer-disputes" }
    ]
  },
  family: {
    title: "Family Law",
    description: "Our expertise covers all aspects of family matters including divorce, maintenance, custody, and domestic violence issues under various personal laws.",
    areas: [
      { name: "Divorce Proceedings", path: "/divorce-proceedings" },
      { name: "Child Custody", path: "/child-custody" },
      { name: "Maintenance & Alimony", path: "/maintenance-alimony" },
      { name: "Domestic Violence Cases", path: "/domestic-violence" },
      { name: "Succession & Inheritance", path: "/succession-inheritance" },
      { name: "Adoption & Guardianship", path: "/adoption-guardianship" }
    ]
  },
  criminal: {
    title: "Criminal Law",
    description: "We provide comprehensive criminal defense services covering all aspects of criminal procedure, bail applications, and trial representation.",
    areas: [
      { name: "FIR Drafting/Quashing", path: "/fir-drafting-quashing" },
      { name: "Bail Applications", path: "/bail-applications" },
      { name: "Trial Defense", path: "/trial-defense" },
      { name: "White-collar Crime", path: "/white-collar-crime" },
      { name: "Economic Offences", path: "/economic-offences" },
      { name: "Appeals & Revisions", path: "/appeals-revisions" }
    ]
  },
  constitutional: {
    title: "Constitutional & Administrative Law",
    description: "We represent clients in constitutional matters, challenges to government actions, and public interest litigation before the High Courts and Supreme Court.",
    areas: [
      { name: "Writ Petitions", path: "/writ-petitions" },
      { name: "Government Action Challenges", path: "/government-action-challenges" },
      { name: "Public Interest Litigation", path: "/public-interest-litigation" },
      { name: "Service Matters", path: "/service-matters" },
      { name: "Fundamental Rights", path: "/fundamental-rights" },
      { name: "Administrative Appeals", path: "/administrative-appeals" }
    ]
  }
};

// ===============================================================
// CONTENT DATA - Used for Page Content
// ===============================================================

// Sub-services data
export const subServiceData = {
  // Corporate Law sub-services
  'shareholders-agreements': {
    title: "Shareholders' Agreements",
    content: "Our team drafts and negotiates comprehensive shareholders' agreements that protect investor rights, establish governance structures, and provide mechanisms for dispute resolution and business exit. We ensure these agreements align with your business objectives while complying with Indian corporate law.",
    parentService: 'corporate-legal',
    relatedServices: [
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' },
      { title: 'Company Formation', path: '/company-formation' },
      { title: 'Corporate Governance', path: '/corporate-governance' },
      { title: 'Due Diligence', path: '/due-diligence' }
    ]
  },
  'corporate-governance': {
    title: "Corporate Governance",
    content: "We advise boards, executives, and shareholders on governance best practices, regulatory compliance, and fiduciary duties. Our governance solutions help organizations balance innovation with appropriate risk management and stakeholder accountability.",
    parentService: 'corporate-legal',
    relatedServices: [
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' },
      { title: "Shareholders' Agreements", path: '/shareholders-agreements' },
      { title: 'Regulatory Compliance', path: '/regulatory-compliance' },
      { title: 'Due Diligence', path: '/due-diligence' }
    ]
  },
  'regulatory-compliance': {
    title: "Regulatory Compliance",
    content: "We provide comprehensive compliance solutions to help businesses navigate India's complex regulatory landscape including Companies Act, SEBI regulations, FEMA, competition law, and sector-specific requirements. Our proactive approach helps prevent legal issues before they arise.",
    parentService: 'corporate-legal',
    relatedServices: [
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' },
      { title: 'Corporate Governance', path: '/corporate-governance' },
      { title: 'Company Formation', path: '/company-formation' },
      { title: 'Due Diligence', path: '/due-diligence' }
    ]
  },

  // Property Law sub-services
  'title-due-diligence': {
    title: "Title Due Diligence",
    content: "We provide comprehensive legal services for property matters including title verification, property registration, lease agreements, and resolving property disputes. Our expertise covers both residential and commercial real estate matters.",
    parentService: 'property-matters',
    relatedServices: [
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Sale & Lease Agreements', path: '/sale-lease-agreements' },
      { title: 'Property Registration', path: '/property-registration' },
      { title: 'Documentation Services', path: '/documentation-services' }
    ]
  },
  'sale-lease-agreements': {
    title: "Sale & Lease Agreements",
    content: "We draft, review, and negotiate robust sale deeds, lease agreements, and other property-related contracts that protect our clients' interests while ensuring compliance with applicable laws including the Transfer of Property Act and state-specific regulations.",
    parentService: 'property-matters',
    relatedServices: [
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Title Due Diligence', path: '/title-due-diligence' },
      { title: 'Property Registration', path: '/property-registration' },
      { title: 'Documentation Services', path: '/documentation-services' }
    ]
  },
  'property-registration': {
    title: "Property Registration",
    content: "We assist with all aspects of property registration including document preparation, verification of encumbrances, payment of stamp duty, and representation before registration authorities. Our services ensure secure, legally valid property transactions.",
    parentService: 'property-matters',
    relatedServices: [
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Title Due Diligence', path: '/title-due-diligence' },
      { title: 'Sale & Lease Agreements', path: '/sale-lease-agreements' },
      { title: 'Documentation Services', path: '/documentation-services' }
    ]
  },
  'rera-compliance': {
    title: "RERA Compliance",
    content: "We provide specialized guidance on Real Estate Regulatory Authority (RERA) compliance for developers, agents, and buyers. Our services include project registration, contract review, and representation in RERA proceedings to protect stakeholder rights.",
    parentService: 'property-matters',
    relatedServices: [
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Builder-Buyer Disputes', path: '/builder-buyer-disputes' },
      { title: 'Sale & Lease Agreements', path: '/sale-lease-agreements' },
      { title: 'Civil Suits', path: '/civil-disputes' }
    ]
  },
  'land-acquisition': {
    title: "Land Acquisition",
    content: "We advise on matters related to land acquisition, compensation determination, rehabilitation, and resettlement under the Land Acquisition Act. Our team represents both landowners and acquiring entities throughout the acquisition process.",
    parentService: 'property-matters',
    relatedServices: [
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Title Due Diligence', path: '/title-due-diligence' },
      { title: 'Civil Suits', path: '/civil-disputes' },
      { title: 'Writ Petitions', path: '/high-court' }
    ]
  },
  'builder-buyer-disputes': {
    title: "Builder-Buyer Disputes",
    content: "We represent clients in disputes related to delayed possession, construction defects, unfair practices, and contractual violations. Our expertise extends to proceedings before consumer forums, RERA authorities, and civil courts.",
    parentService: 'property-matters',
    relatedServices: [
      { title: 'Property Law', path: '/property-matters' },
      { title: 'RERA Compliance', path: '/rera-compliance' },
      { title: 'Civil Suits', path: '/civil-disputes' },
      { title: 'Consumer Protection', path: '/consumer-disputes' }
    ]
  },

  // Family Law sub-services (additional ones)
  'divorce-proceedings': {
    title: "Divorce Proceedings",
    content: "We handle a wide range of marital and family disputes with sensitivity and dedication. Our family law experts provide compassionate guidance through divorce proceedings, child custody arrangements, and asset division to protect your interests.",
    parentService: 'family-law',
    relatedServices: [
      { title: 'Family Law Services', path: '/family-law' },
      { title: 'Child Custody', path: '/child-custody' },
      { title: 'Maintenance & Alimony', path: '/maintenance-alimony' },
      { title: 'Domestic Violence Cases', path: '/domestic-violence' }
    ]
  },
  'maintenance-alimony': {
    title: "Maintenance & Alimony",
    content: "We represent clients in maintenance and alimony proceedings under various personal laws and the Maintenance and Welfare of Parents and Senior Citizens Act. Our approach balances financial justice with practical considerations of each case.",
    parentService: 'family-law',
    relatedServices: [
      { title: 'Marital Disputes', path: '/marital-disputes' },
      { title: 'Divorce Proceedings', path: '/divorce-proceedings' },
      { title: 'Family Law Services', path: '/family-law' },
      { title: 'Child Custody', path: '/child-custody' }
    ]
  },
  'succession-inheritance': {
    title: "Succession & Inheritance",
    content: "We advise on succession and inheritance matters under various personal laws including Hindu Succession Act, Indian Succession Act, and Muslim personal law. Our services include will drafting, probate proceedings, and representation in inheritance disputes.",
    parentService: 'family-law',
    relatedServices: [
      { title: 'Family Law Services', path: '/family-law' },
      { title: 'Documentation Services', path: '/documentation-services' },
      { title: 'Property Matters', path: '/property-matters' },
      { title: 'Civil Suits', path: '/civil-disputes' }
    ]
  },
  'adoption-guardianship': {
    title: "Adoption & Guardianship",
    content: "We guide clients through the legal complexities of adoption under the Hindu Adoption and Maintenance Act, Juvenile Justice Act, and guardianship proceedings under the Guardians and Wards Act ensuring compliance with all legal requirements.",
    parentService: 'family-law',
    relatedServices: [
      { title: 'Family Law Services', path: '/family-law' },
      { title: 'Child Custody', path: '/child-custody' },
      { title: 'Documentation Services', path: '/documentation-services' },
      { title: 'Constitutional Matters', path: '/constitutional-matters' }
    ]
  },

  // Criminal Law sub-services
  'fir-drafting-quashing': {
    title: 'FIR Drafting/Quashing',
    content: 'We assist with filing well-drafted FIRs for victims of crime and also represent accused persons seeking quashing of FIRs under Section 482 of CrPC. Our strategic approach aims to protect our clients\' rights at this critical initial stage.',
    parentService: 'criminal-case',
    relatedServices: [
      { title: 'Criminal Defense', path: '/criminal-case' },
      { title: 'Bail Applications', path: '/bail-applications' },
      { title: 'Trial Defense', path: '/trial-defense' },
      { title: 'Constitutional Matters', path: '/constitutional-matters' }
    ]
  },
  'bail-applications': {
    title: 'Bail Applications',
    content: 'We handle regular bail, anticipatory bail, and interim bail applications at all court levels. Our criminal law team prepares comprehensive applications that effectively present legal and factual grounds for bail consideration.',
    parentService: 'criminal-case',
    relatedServices: [
      { title: 'Criminal Defense', path: '/criminal-case' },
      { title: 'FIR Drafting/Quashing', path: '/fir-drafting-quashing' },
      { title: 'Trial Defense', path: '/trial-defense' },
      { title: 'Appeals & Revisions', path: '/appeals-revisions' }
    ]
  },
  'trial-defense': {
    title: 'Trial Defense',
    content: 'Our criminal law specialists provide comprehensive legal support through all stages of criminal proceedings. From representation during investigations to courtroom defense, we ensure your rights are protected and the best possible outcome is secured.',
    parentService: 'criminal-case',
    relatedServices: [
      { title: 'Criminal Defense', path: '/criminal-case' },
      { title: 'FIR Drafting/Quashing', path: '/fir-drafting-quashing' },
      { title: 'Bail Applications', path: '/bail-applications' },
      { title: 'Appeals & Revisions', path: '/appeals-revisions' }
    ]
  },
  'white-collar-crime': {
    title: 'White-collar Crime',
    content: 'We defend clients facing allegations of financial crimes, fraud, corporate misconduct, and corruption. Our specialized team has expertise in handling complex cases under various financial and economic legislation including Prevention of Money Laundering Act.',
    parentService: 'criminal-case',
    relatedServices: [
      { title: 'Criminal Defense', path: '/criminal-case' },
      { title: 'Economic Offences', path: '/economic-offences' },
      { title: 'Trial Defense', path: '/trial-defense' },
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' }
    ]
  },
  'economic-offences': {
    title: 'Economic Offences',
    content: 'We provide defense representation in cases involving economic offenses including tax evasion, securities fraud, and foreign exchange violations. Our team has extensive experience dealing with specialized agencies like ED, SFIO, and Income Tax authorities.',
    parentService: 'criminal-case',
    relatedServices: [
      { title: 'Criminal Defense', path: '/criminal-case' },
      { title: 'White-collar Crime', path: '/white-collar-crime' },
      { title: 'Trial Defense', path: '/trial-defense' },
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' }
    ]
  },
  'appeals-revisions': {
    title: 'Appeals & Revisions',
    content: 'We represent clients in criminal appeals, revisions, and review petitions before Sessions Courts, High Courts, and the Supreme Court. Our appellate practice focuses on identifying procedural and substantive errors in lower court judgments.',
    parentService: 'criminal-case',
    relatedServices: [
      { title: 'Criminal Defense', path: '/criminal-case' },
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Trial Defense', path: '/trial-defense' },
      { title: 'Bail Applications', path: '/bail-applications' }
    ]
  },
  
  // Constitutional Law sub-services
  'writ-petitions': {
    title: 'Writ Petitions',
    content: 'We have extensive experience in filing and defending writ petitions before the High Courts of Allahabad, Delhi, Mumbai and other jurisdictions. Our expertise covers matters involving government actions, fundamental rights, and statutory interpretations.',
    parentService: 'constitutional-matters',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Public Interest Litigation', path: '/public-interest-litigation' },
      { title: 'Government Action Challenges', path: '/government-action-challenges' },
      { title: 'Fundamental Rights', path: '/fundamental-rights' }
    ]
  },
  'government-action-challenges': {
    title: 'Government Action Challenges',
    content: 'We represent clients challenging arbitrary, unreasonable, or unconstitutional government actions through writ petitions. Our constitutional law team has successfully challenged various executive and administrative actions that exceed constitutional boundaries.',
    parentService: 'constitutional-matters',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Writ Petitions', path: '/writ-petitions' },
      { title: 'Public Interest Litigation', path: '/public-interest-litigation' },
      { title: 'Service Matters', path: '/service-matters' }
    ]
  },
  'public-interest-litigation': {
    title: 'Public Interest Litigation',
    content: 'We file and defend public interest litigations addressing significant social, environmental, and policy issues. Our PIL practice aims to secure constitutional rights for disadvantaged groups and hold public authorities accountable.',
    parentService: 'constitutional-matters',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Writ Petitions', path: '/writ-petitions' },
      { title: 'Fundamental Rights', path: '/fundamental-rights' },
      { title: 'Government Action Challenges', path: '/government-action-challenges' }
    ]
  },
  'service-matters': {
    title: 'Service Matters',
    content: 'We represent government employees, PSU staff, and other service personnel in matters related to promotion, disciplinary proceedings, transfers, and retirement benefits before administrative tribunals and High Courts.',
    parentService: 'constitutional-matters',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Government Action Challenges', path: '/government-action-challenges' },
      { title: 'Writ Petitions', path: '/writ-petitions' },
      { title: 'Administrative Appeals', path: '/administrative-appeals' }
    ]
  },
  'fundamental-rights': {
    title: 'Fundamental Rights',
    content: 'We handle complex constitutional law cases involving fundamental rights, challenges to government actions, and interpretation of constitutional provisions. Our expertise extends to cases before the High Courts and Supreme Court of India.',
    parentService: 'constitutional-matters',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Writ Petitions', path: '/writ-petitions' },
      { title: 'Public Interest Litigation', path: '/public-interest-litigation' },
      { title: 'Government Action Challenges', path: '/government-action-challenges' }
    ]
  },
  'administrative-appeals': {
    title: 'Administrative Appeals',
    content: 'We represent clients in appeals against administrative decisions of various regulatory bodies, tribunals, and commissions. Our approach involves thorough scrutiny of procedural compliance and substantive decision-making processes.',
    parentService: 'constitutional-matters',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Service Matters', path: '/service-matters' },
      { title: 'Writ Petitions', path: '/writ-petitions' },
      { title: 'Government Action Challenges', path: '/government-action-challenges' }
    ]
  },
  
  // Family Law sub-services
  'child-custody': {
    title: 'Child Custody',
    content: 'We provide expert legal representation in child custody matters, focusing on the best interests of the child while protecting parental rights. Our family law attorneys handle custody determinations, modifications, and enforcement proceedings with sensitivity and dedication.',
    parentService: 'family-law',
    relatedServices: [
      { title: 'Family Law Services', path: '/family-law' },
      { title: 'Marital Disputes', path: '/marital-disputes' },
      { title: 'Domestic Violence Cases', path: '/domestic-violence' },
      { title: 'Documentation Services', path: '/documentation-services' }
    ]
  },
  'domestic-violence': {
    title: 'Domestic Violence Cases',
    content: 'We provide legal representation and support for victims of domestic violence under the Protection of Women from Domestic Violence Act. Our services include securing protection orders, residence orders, and compensation while ensuring client safety.',
    parentService: 'family-law',
    relatedServices: [
      { title: 'Family Law Services', path: '/family-law' },
      { title: 'Marital Disputes', path: '/marital-disputes' },
      { title: 'Child Custody', path: '/child-custody' },
      { title: 'Criminal Defense', path: '/criminal-case' }
    ]
  }
};

// Practice Area Subsections
export const practiceAreaData = {
  'civil-disputes': {
    title: 'Civil Suits',
    content: 'Our civil litigation team represents clients in a wide range of civil disputes including property matters, contract disputes, recovery suits, and tort claims. We handle cases at all levels of the court system with a strategic approach to achieve favorable outcomes.',
    relatedServices: [
      { title: 'Arbitration & Mediation', path: '/arbitration' },
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Contract Management', path: '/contract-management' },
      { title: 'Commercial Disputes', path: '/commercial-disputes' }
    ]
  },
  'criminal-case': {
    title: 'Criminal Defense',
    content: 'Our criminal law specialists provide comprehensive legal support through all stages of criminal proceedings. From representation during investigations to courtroom defense, we ensure your rights are protected and the best possible outcome is secured.',
    relatedServices: [
      { title: 'Bail Applications', path: '/bail-applications' },
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Writ Petitions', path: '/writ-petitions' },
      { title: 'Appeals & Revisions', path: '/appeals-revisions' }
    ]
  },
  'constitutional-matters': {
    title: 'Constitutional Matters',
    content: 'We handle complex constitutional law cases involving fundamental rights, challenges to government actions, and interpretation of constitutional provisions. Our expertise extends to cases before the High Courts and Supreme Court of India.',
    relatedServices: [
      { title: 'Writ Petitions', path: '/writ-petitions' },
      { title: 'Public Interest Litigation', path: '/public-interest-litigation' },
      { title: 'Fundamental Rights', path: '/fundamental-rights' },
      { title: 'Criminal Defense', path: '/criminal-case' }
    ]
  },
  'arbitration': {
    title: 'Arbitration & Mediation',
    content: 'We offer comprehensive alternative dispute resolution services including arbitration, mediation, and conciliation. Our team helps clients resolve disputes efficiently without the delays and costs associated with traditional litigation.',
    relatedServices: [
      { title: 'Civil Suits', path: '/civil-disputes' },
      { title: 'Commercial Disputes', path: '/commercial-disputes' },
      { title: 'Contract Management', path: '/contract-management' },
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' }
    ]
  },
  'commercial-disputes': {
    title: 'Commercial Disputes',
    content: 'We handle complex commercial litigation including partnership disputes, shareholder conflicts, breach of contract matters, and business torts. Our team combines deep legal knowledge with business acumen to protect your commercial interests.',
    relatedServices: [
      { title: 'Contract Management', path: '/contract-management' },
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' },
      { title: 'Arbitration & Mediation', path: '/arbitration' },
      { title: 'Civil Suits', path: '/civil-disputes' }
    ]
  },
  'high-court': {
    title: 'Writ Petitions & High Court Practice',
    content: 'We have extensive experience in filing and defending writ petitions before the High Courts of Allahabad, Delhi, Mumbai and other jurisdictions. Our expertise covers matters involving government actions, fundamental rights, and statutory interpretations.',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Criminal Law', path: '/criminal-case' },
      { title: 'Civil Disputes', path: '/civil-disputes' },
      { title: 'Public Interest Litigation', path: '/public-interest-litigation' }
    ]
  }
};

// Service Pages
export const serviceData = {
  'marital-disputes': {
    title: 'Marital Disputes',
    content: 'We handle a wide range of marital and family disputes with sensitivity and dedication. Our family law experts provide compassionate guidance through divorce proceedings, child custody arrangements, and asset division to protect your interests.',
    relatedServices: [
      { title: 'Divorce Proceedings', path: '/divorce-proceedings' },
      { title: 'Maintenance & Alimony', path: '/maintenance-alimony' },
      { title: 'Child Custody', path: '/child-custody' },
      { title: 'Domestic Violence Cases', path: '/domestic-violence' }
    ]
  },
  'contract-management': {
    title: 'Contract Management',
    content: 'Our contract management services provide end-to-end solutions for creating, negotiating, and maintaining effective contractual agreements. We help minimize risk while maximizing operational efficiency across your organization.',
    relatedServices: [
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' },
      { title: 'Commercial Disputes', path: '/commercial-disputes' },
      { title: 'Civil Suits', path: '/civil-disputes' },
      { title: 'Documentation Services', path: '/documentation-services' }
    ]
  },
  'property-matters': {
    title: 'Property Law',
    content: 'We provide comprehensive legal services for property matters including title verification, property registration, lease agreements, and resolving property disputes. Our expertise covers both residential and commercial real estate matters.',
    relatedServices: [
      { title: 'Title Due Diligence', path: '/title-due-diligence' },
      { title: 'Sale & Lease Agreements', path: '/sale-lease-agreements' },
      { title: 'Property Registration', path: '/property-registration' },
      { title: 'RERA Compliance', path: '/rera-compliance' }
    ]
  },
  'family-law': {
    title: 'Family Law Services',
    content: "We provide comprehensive assistance for family matters including divorce proceedings, child custody arrangements, maintenance claims, domestic violence protection, and succession planning. Our approach is centered on protecting your family's best interests while minimizing conflict.",
    relatedServices: [
      { title: 'Divorce Proceedings', path: '/divorce-proceedings' },
      { title: 'Child Custody', path: '/child-custody' },
      { title: 'Succession & Inheritance', path: '/succession-inheritance' },
      { title: 'Adoption & Guardianship', path: '/adoption-guardianship' }
    ]
  },
  'high-court-practice': {
    title: 'High Court Practice',
    content: 'We represent clients in the Allahabad High Court, Delhi High Court, Mumbai High Court, and other judicial forums across North India. Our team has extensive experience in handling civil appeals, criminal appeals, writ petitions, constitutional matters, and public interest litigations.',
    relatedServices: [
      { title: 'Constitutional Matters', path: '/constitutional-matters' },
      { title: 'Criminal Defense', path: '/criminal-case' },
      { title: 'Civil Suits', path: '/civil-disputes' },
      { title: 'Writ Petitions', path: '/high-court' }
    ]
  },
  'documentation-services': {
    title: 'Documentation Services',
    content: 'We prepare, review, and register legal documents including property agreements, wills, partnership deeds, affidavits, and notices while ensuring compliance with relevant laws and regulations. Our meticulous attention to detail helps prevent future disputes and legal complications.',
    relatedServices: [
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Family Law Services', path: '/family-law' },
      { title: 'Contract Management', path: '/contract-management' },
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' }
    ]
  },
  'corporate-legal': {
    title: 'Corporate Legal Advisory',
    content: 'We provide comprehensive legal counsel to businesses on regulatory compliance, corporate governance, contract drafting and review, dispute resolution, and general business legal matters. Our services help businesses navigate complex legal landscapes while minimizing risk exposure.',
    relatedServices: [
      { title: 'Shareholders\'  Agreements', path: '/shareholders-agreements' },
      { title: 'Corporate Governance', path: '/corporate-governance' },
      { title: 'Regulatory Compliance', path: '/regulatory-compliance' },
      { title: 'Due Diligence', path: '/due-diligence' }
    ]
  },
  'company-formation': {
    title: 'Company Formation',
    content: 'We assist entrepreneurs and business owners with all aspects of company formation and structuring, including incorporation, partnership agreements, LLP registration, and compliance with regulatory requirements. Our team ensures your business begins with a solid legal foundation.',
    relatedServices: [
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' },
      { title: 'Due Diligence', path: '/due-diligence' },
      { title: 'Contract Management', path: '/contract-management' },
      { title: 'Documentation Services', path: '/documentation-services' }
    ]
  },
  'due-diligence': {
    title: 'Due Diligence',
    content: 'Our due diligence services provide thorough legal analysis and risk assessment for business transactions, property acquisitions, and investments. We conduct comprehensive reviews to identify potential legal issues and ensure informed decision-making.',
    relatedServices: [
      { title: 'Corporate Legal Advisory', path: '/corporate-legal' },
      { title: 'Company Formation', path: '/company-formation' },
      { title: 'Property Law', path: '/property-matters' },
      { title: 'Contract Management', path: '/contract-management' }
    ]
  }
};

// Default page data
export const defaultPageData = {
  title: 'Legal Services',
  content: 'LegalSutra provides comprehensive legal services to businesses and individuals across multiple practice areas. Our expert team combines legal knowledge with innovative approaches to deliver exceptional results for our clients.',
  relatedServices: [
    { title: 'Civil Suits', path: '/civil-disputes' },
    { title: 'Criminal Defense', path: '/criminal-case' },
    { title: 'Family Law Services', path: '/family-law' },
    { title: 'Corporate Legal Advisory', path: '/corporate-legal' }
  ]
};

// Combine all data for easier access
export const allPageData = {
  ...practiceAreaData,
  ...serviceData,
  ...subServiceData,
  'default': defaultPageData
};